import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { hostname, parseError } from "../../utils";

export default ({
  open,
  onClose,
  inbox,
  getToken,
  eventListener,
  companyId,
}: {
  open: boolean;
  onClose: Function;
  inbox: any;
  getToken: Function;
  eventListener: Function;
  companyId: string;
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<any>({});

  const handleClose = () => {
    onClose();
  };

  const onSave = async () => {
    setLoading(true);
    const token = await getToken();
    
    // Determine if we're creating or updating
    const isUpdate = inbox && inbox.id;
    const method = isUpdate ? 'put' : 'post';
    const url = isUpdate ? `${hostname}/inboxes/${inbox.id}` : `${hostname}/inboxes`;
    
    axios[method](
      url,
      { ...formData, companyId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((res) => {
        handleClose();
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (inbox) {
      setFormData({
        ...inbox,
        sharing: inbox.sharing || 'Private',
        // syncEnabled defaults to true (null/undefined means enabled)
        syncEnabled: inbox.syncEnabled !== false
      });
    } else {
      // Reset form when creating a new inbox
      setFormData({ name: '', domain: '', sharing: 'Private', syncEnabled: true });
    }
  }, [inbox]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {inbox ? 'Edit Inbox' : 'New Inbox'}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!inbox && (
          <DialogContentText>
            Create a new inbox for your company and setup your domain or subdomain
            (ex: example.com, ai.example.com,sales.example.com) to forward to your
            unique domain address. This allows you to receive emails from your
            customers and automate your replies. Use https://forwardemail.net/ to
            setup email forwarding for your companies domain.
          </DialogContentText>
        )}
        <TextField
          sx={{ mt: 2 }}
          fullWidth
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <TextField
          sx={{ mt: 2 }}
          fullWidth
          label="Domain Name"
          value={formData.domain}
          onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
        />
        <FormGroup sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.sharing === "Shared"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sharing: e.target.checked ? "Shared" : "Private"
                  })
                }
              />
            }
            label="Share with company members"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.syncEnabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    syncEnabled: e.target.checked
                  })
                }
              />
            }
            label="Sync incoming emails"
          />
        </FormGroup>
        <DialogContentText sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
          {formData.syncEnabled
            ? "Incoming emails will be synced into the system."
            : "Emails will not be synced. You can still send emails from this inbox."}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <LoadingButton onClick={onSave} variant={"contained"} loading={loading}>
          {inbox ? 'Update' : 'Create'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
